import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import type { IconName } from '@hotjar/hotsauce';
import { Badge, Icon, Divider, Text, tokens } from '@hotjar/hotsauce';

import { ListItem } from '@hotjar/webapp/components/DropdownMenu';
import {
  useMsTeamsForwardingSettings,
  useSlackForwardingSettings,
  useWebhooksForwardingSettings,
} from '@hotjar/integrations';
import type { WidgetType, WidgetId } from '@hotjar/integrations';
import { ErrorBoundary } from '@hotjar/error-handler';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { prefilledTemplates } from './ForwardToZapier';

const DropdownContentContainer = styled.div`
  border-radius: ${tokens.spacer.x0};
  color: ${tokens.color.text_default};
  width: 280px;
`;

const ForwardingType = styled.span`
  display: grid;
  align-self: center;
  grid-template-columns: auto 1fr auto;
`;

const StyledIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

export interface ForwardDropdownProps {
  siteId: number;
  widgetType: WidgetType;
  widgetId: WidgetId;
  onEdit: () => void;
  hasEmailForwarding: boolean;
  hasSlackForwarding: boolean;
  hasMsTeamsForwarding: boolean;
  hasWebhooksForwarding: boolean;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  hasZapierEnabled: boolean;
  emailEnabled: boolean;
  // FIXME: Use this prop or remove it if not needed.
  // eslint-disable-next-line react/no-unused-prop-types
  trackingUiPosition?: string;
  trackingState?: string;
}

export const ForwardDropdown = ({
  siteId,
  widgetType,
  onEdit,
  hasEmailForwarding,
  hasSlackForwarding,
  hasMsTeamsForwarding,
  hasWebhooksForwarding,
  emailEnabled,
  widgetId,
  trackingState,
}: ForwardDropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const [slackForwardingSettings] = useSlackForwardingSettings(siteId, widgetType, widgetId);
  const msTeamsSettings = useMsTeamsForwardingSettings(
    siteId,
    widgetType,
    widgetId,
    !hasMsTeamsForwarding,
  );

  const webhooksSettings = useWebhooksForwardingSettings({
    siteId,
    widgetType,
    widgetId,
    disabled: !hasWebhooksForwarding,
  });

  const { enabled: slackEnabled } = slackForwardingSettings ?? { enabled: false };

  const { click: trackClick } = useTrack();

  const track = (uiPosition: string) => {
    trackClick('Forward Icon', uiPosition, {
      state_event_was_triggered_on: trackingState,
    });
  };

  const zapierTemplates = hasMsTeamsForwarding
    ? prefilledTemplates.filter((template) => template.icon !== 'microsoft_teams')
    : prefilledTemplates;

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <DropdownContentContainer>
        {hasSlackForwarding && (
          <ListItem
            onClick={() => {
              track('Forward to Slack');
              onEdit();
            }}
          >
            <ForwardingType>
              <StyledIcon icon="slack" />
              <Text textType="body2">{t('integrations:slack.name')}</Text>
              <Badge kind={slackEnabled ? 'active' : 'inactive'} size="s" />
            </ForwardingType>
          </ListItem>
        )}

        {hasMsTeamsForwarding && (
          <>
            <Divider />
            <ListItem
              onClick={() => {
                track('Forward to Microsoft Teams');
                onEdit();
              }}
            >
              <ForwardingType>
                <StyledIcon icon="microsoft_teams" />
                <Text textType="body2">{t('integrations:ms_teams.name')}</Text>
                <Badge kind={msTeamsSettings?.enabled ? 'active' : 'inactive'} size="s" />
              </ForwardingType>
            </ListItem>
          </>
        )}

        {hasWebhooksForwarding && (
          <>
            <Divider />
            <ListItem
              onClick={() => {
                track('Forward to Webhooks');
                onEdit();
              }}
            >
              <ForwardingType>
                <StyledIcon icon="webhooks" />
                <Text textType="body2">{t('integrations:webhooks.name')}</Text>
                <Badge kind={webhooksSettings?.enabled ? 'active' : 'inactive'} size="s" />
              </ForwardingType>
            </ListItem>
          </>
        )}

        {hasEmailForwarding && (
          <>
            <Divider />
            <ListItem
              onClick={() => {
                track('Forward to E-mail');
                onEdit();
              }}
            >
              <ForwardingType>
                <StyledIcon icon="email" />
                <Text textType="body2">{t('integrations:email.name')}</Text>
                <Badge kind={emailEnabled ? 'active' : 'inactive'} size="s" />
              </ForwardingType>
            </ListItem>
          </>
        )}
        <Divider />
        {zapierTemplates.map(({ name, icon, surveyUrl, feedbackUrl }, index) => (
          <ListItem
            key={name}
            onClick={() => {
              const uiPosition =
                name === 'Zapier' ? 'Forward to Zapier' : `Forward to Zapier - ${name}`;
              track(uiPosition);
            }}
          >
            <a
              href={widgetType === 'incoming_feedback' ? feedbackUrl : surveyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ForwardingType>
                <StyledIcon icon={icon as IconName} size="m" />
                <Text textType="body2">
                  {`${name} ${
                    index === zapierTemplates.length - 1
                      ? ''
                      : t('common:survey.creation.forwarding.with-zapier')
                  }`}
                </Text>
                <StyledIcon icon="open_new_window" color="text_default" />
              </ForwardingType>
            </a>
          </ListItem>
        ))}
      </DropdownContentContainer>
    </ErrorBoundary>
  );
};
